// iter-121.116 Phase 2 — Platform Cockpit scope bar.
//
// Sticky top bar above the SA Home page. Shows:
//   • Greeting + dynamic health pill ("All clear" or "N items need attention")
//   • Period selector (7d / 30d / 90d / MTD / QTD / YTD)
//   • Cascading filters: Org → Tenant → Owner
//   • "Save as default view" button
//
// Lives ABOVE the legacy `HOME_WIDGET_REGISTRY` widgets so it can drive
// re-fetches across the whole page via `CockpitScopeContext`.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Bookmark, Building2, Calendar, CheckCircle2, AlertTriangle, User } from "lucide-react";
import { toast } from "sonner";
import { useCockpitScope } from "@/lib/cockpit-scope";

const PERIOD_OPTIONS = [
    { value: "7d",  label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "mtd", label: "Month to date" },
    { value: "qtd", label: "Quarter to date" },
    { value: "ytd", label: "Year to date" },
    { value: "custom", label: "Custom range…" },
];

const SENTINEL_ALL = "__all__"; // shadcn Select rejects empty-string values
function parseLocalDate(value) {
    if (!value) return undefined;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
}

function toLocalIsoDate(date) {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function HealthPill() {
    const { queryString } = useCockpitScope();
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        api.get(`/home/cockpit/actions?${queryString}`)
            .then((r) => { if (alive) setData(r.data || null); })
            .catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [queryString]);
    if (!data) {
        return (
            <div className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-sm border border-border bg-secondary/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground shrink-0"
                 data-testid="cockpit-health-loading">
                Loading platform health…
            </div>
        );
    }
    if (data.all_clear) {
        return (
            <div className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[11px] font-mono uppercase tracking-wider shrink-0"
                 data-testid="cockpit-health-pill"
                 data-state="clear">
                <CheckCircle2 className="h-3.5 w-3.5" />
                All systems healthy
            </div>
        );
    }
    return (
        <div className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-sm border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11px] font-mono uppercase tracking-wider shrink-0"
             data-testid="cockpit-health-pill"
             data-state="attention">
            <AlertTriangle className="h-3.5 w-3.5" />
            {data.total_action_count} {data.total_action_count === 1 ? "item" : "items"} need attention
        </div>
    );
}

export default function CockpitScopeBar({ greeting }) {
    const {
        scope, options, optionsLoading, saving,
        setPeriod, setCustomRange, setOrgId, setTenantId, setOwnerId,
        saveAsDefault,
    } = useCockpitScope();

    const onSave = async () => {
        const ok = await saveAsDefault();
        if (ok) toast.success("Default view saved");
        else toast.error("Failed to save default view");
    };

    // Format the custom range button label (e.g. "Jan 1 → Feb 1").
    const customLabel = (() => {
        if (scope.period !== "custom") return "Custom range";
          const start = parseLocalDate(scope.custom_start);
        const end = parseLocalDate(scope.custom_end);
        const showYear = start && end && start.getFullYear() !== end.getFullYear();
        const fmt = (d) => d
            ? d.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                ...(showYear ? { year: "numeric" } : {}),
            })
            : "?";
        return `${fmt(start)} -> ${fmt(end)}`;
    })();

    return (
        <div className="border border-border rounded-sm bg-card p-3 mb-4"
             data-testid="cockpit-scope-bar">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                {/* Left: greeting + health pill */}
                <div className="flex items-center gap-3 min-w-0">
                    {greeting && (
                        <div className="text-sm font-medium truncate" data-testid="cockpit-greeting">
                            {greeting}
                        </div>
                    )}
                    <HealthPill />
                </div>

                {/* Right: scope controls */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Period */}
                    <Select value={scope.period} onValueChange={setPeriod}>
                        <SelectTrigger className="h-8 w-[150px] rounded-sm text-[12px]" data-testid="cockpit-period-select">
                            <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                            <SelectValue placeholder="Period" />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIOD_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value} data-testid={`cockpit-period-${o.value}`}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Custom date-range popover — only when period === custom */}
                    {scope.period === "custom" && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm"
                                        className="h-8 rounded-sm text-[12px] font-normal"
                                        data-testid="cockpit-custom-range-trigger">
                                    <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                    {customLabel}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-auto p-0"
                                            data-testid="cockpit-custom-range-popover">
                                <CalendarPicker
                                    mode="range"
                                    selected={{
                                        from: parseLocalDate(scope.custom_start),
                                        to:   parseLocalDate(scope.custom_end),
                                    }}
                                    onSelect={(range) => {
                                        if (range?.from) {
                                            setCustomRange(
                                                toLocalIsoDate(range.from),
                                                toLocalIsoDate(range.to || range.from),
                                            );
                                        }
                                    }}
                                    numberOfMonths={2}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    )}

                    {/* Org */}
                    <Select
                        value={scope.org_id || SENTINEL_ALL}
                        onValueChange={(v) => setOrgId(v === SENTINEL_ALL ? null : v)}
                        disabled={optionsLoading}
                    >
                        <SelectTrigger className="h-8 w-[160px] rounded-sm text-[12px]" data-testid="cockpit-org-select">
                            <Building2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                            <SelectValue placeholder="All orgs" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={SENTINEL_ALL} data-testid="cockpit-org-all">All orgs</SelectItem>
                            {options.orgs.map((o) => (
                                <SelectItem key={o.id} value={o.id} data-testid={`cockpit-org-${o.id}`}>
                                    {o.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Tenant (only meaningful when Org picked OR there are multiple tenants) */}
                    <Select
                        value={scope.tenant_id || SENTINEL_ALL}
                        onValueChange={(v) => setTenantId(v === SENTINEL_ALL ? null : v)}
                        disabled={optionsLoading || options.tenants.length === 0}
                    >
                        <SelectTrigger className="h-8 w-[160px] rounded-sm text-[12px]" data-testid="cockpit-tenant-select">
                            <SelectValue placeholder="All entities" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={SENTINEL_ALL} data-testid="cockpit-tenant-all">
                                {scope.org_id ? "All entities in org" : "All entities"}
                            </SelectItem>
                            {options.tenants.map((t) => (
                                <SelectItem key={t.id} value={t.id} data-testid={`cockpit-tenant-${t.id}`}>
                                    {t.name}{t.lifecycle === "trial" ? " · trial" : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Owner */}
                    <Select
                        value={scope.owner_id || SENTINEL_ALL}
                        onValueChange={(v) => setOwnerId(v === SENTINEL_ALL ? null : v)}
                        disabled={optionsLoading || options.owners.length === 0}
                    >
                        <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="cockpit-owner-select">
                            <User className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                            <SelectValue placeholder="All owners" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={SENTINEL_ALL} data-testid="cockpit-owner-all">All owners</SelectItem>
                            {options.owners.map((u) => (
                                <SelectItem key={u.id} value={u.id} data-testid={`cockpit-owner-${u.id}`}>
                                    {u.name || u.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Save as default */}
                    <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-8"
                        onClick={onSave}
                        disabled={saving}
                        data-testid="cockpit-save-default"
                    >
                        <Bookmark className="h-3.5 w-3.5 mr-1.5" />
                        {saving ? "Saving…" : "Save as default"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
